/**
 * Centralized status configuration and contextual data extractor for ENGA Quotations.
 * Defines semantic badge styling, display labels, tooltip descriptions, theme color combinations, and context extraction.
 */

export interface StatusTooltipTheme {
  cardClass: string
  headerClass: string
  headerTextClass: string
  tagClass: string
  dotClass: string
  descClass: string
  detailDividerClass: string
  detailLabelClass: string
  detailValueClass: string
}

export interface StatusConfig {
  key: string
  label: string
  badgeClass: string
  dotClass: string
  title: string
  description: string
  tooltipTheme: StatusTooltipTheme
}

export interface StatusContextDetail {
  label: string
  value: string
}

export const DEFAULT_QUOTATION_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "PENDING", label: "Pending" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "REJECTED", label: "Rejected" },
  { value: "REQUEST_REVISION", label: "Revision Requested" },
  { value: "AWAITING_REVISED_QUOTATION", label: "Awaiting Revision" },
  { value: "SUPERSEDED", label: "Superseded" },
]

export const QUOTATION_STATUS_OPTIONS = DEFAULT_QUOTATION_STATUS_OPTIONS

/**
 * Normalizes and resolves a Quotation's operational status.
 */
export function resolveQuotationStatus(item: any): string {
  if (!item) return "PENDING"
  const raw = String(item.status || "").toUpperCase().trim()

  if (raw === "APPROVED") return "ACCEPTED"
  return raw || "PENDING"
}

function formatDateTime(isoStr?: string | null): string | null {
  if (!isoStr) return null
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export function getQuotationStatusConfig(
  resolvedStatus: string,
  item?: any,
  role: "admin" | "seller" | "buyer" = "admin"
): StatusConfig {
  const statusKey = (resolvedStatus || "PENDING").toUpperCase()

  switch (statusKey) {
    case "PENDING":
      return {
        key: "PENDING",
        label: "Pending",
        badgeClass: "bg-amber-50 text-amber-800 border-amber-200/90 hover:bg-amber-100/60",
        dotClass: "bg-amber-500",
        title: "Awaiting Review",
        description: "Quote submitted and awaiting commercial review",
        tooltipTheme: {
          cardClass: "bg-white border-amber-200 shadow-xl shadow-amber-950/10",
          headerClass: "bg-amber-50/80 border-b border-amber-100/90",
          headerTextClass: "text-amber-950",
          tagClass: "text-amber-800 bg-amber-100/80 border-amber-200/80",
          dotClass: "bg-amber-500",
          descClass: "text-slate-600",
          detailDividerClass: "border-amber-100/70",
          detailLabelClass: "text-slate-500",
          detailValueClass: "text-slate-900 font-semibold",
        },
      }

    case "ACCEPTED":
    case "APPROVED":
      return {
        key: "ACCEPTED",
        label: "Accepted",
        badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200/90 hover:bg-emerald-100/60",
        dotClass: "bg-emerald-500",
        title: "Quote Agreed",
        description: "Commercial terms accepted and ready for checkout",
        tooltipTheme: {
          cardClass: "bg-white border-emerald-200 shadow-xl shadow-emerald-950/10",
          headerClass: "bg-emerald-50/80 border-b border-emerald-100/90",
          headerTextClass: "text-emerald-950",
          tagClass: "text-emerald-800 bg-emerald-100/80 border-emerald-200/80",
          dotClass: "bg-emerald-500",
          descClass: "text-slate-600",
          detailDividerClass: "border-emerald-100/70",
          detailLabelClass: "text-slate-500",
          detailValueClass: "text-slate-900 font-semibold",
        },
      }

    case "REJECTED":
      return {
        key: "REJECTED",
        label: "Rejected",
        badgeClass: "bg-rose-50 text-rose-700 border-rose-200/90 hover:bg-rose-100/60",
        dotClass: "bg-rose-500",
        title: "Quote Declined",
        description: "This quotation proposal was declined",
        tooltipTheme: {
          cardClass: "bg-white border-rose-200 shadow-xl shadow-rose-950/10",
          headerClass: "bg-rose-50/80 border-b border-rose-100/90",
          headerTextClass: "text-rose-950",
          tagClass: "text-rose-800 bg-rose-100/80 border-rose-200/80",
          dotClass: "bg-rose-500",
          descClass: "text-slate-600",
          detailDividerClass: "border-rose-100/70",
          detailLabelClass: "text-slate-500",
          detailValueClass: "text-slate-900 font-semibold",
        },
      }

    case "REQUEST_REVISION":
      return {
        key: "REQUEST_REVISION",
        label: "Revision Requested",
        badgeClass: "bg-violet-50 text-violet-700 border-violet-200/90 hover:bg-violet-100/60",
        dotClass: "bg-violet-500",
        title: "Changes Requested",
        description: "Revisions requested on quote price or commercial terms",
        tooltipTheme: {
          cardClass: "bg-white border-violet-200 shadow-xl shadow-violet-950/10",
          headerClass: "bg-violet-50/80 border-b border-violet-100/90",
          headerTextClass: "text-violet-950",
          tagClass: "text-violet-800 bg-violet-100/80 border-violet-200/80",
          dotClass: "bg-violet-500",
          descClass: "text-slate-600",
          detailDividerClass: "border-violet-100/70",
          detailLabelClass: "text-slate-500",
          detailValueClass: "text-slate-900 font-semibold",
        },
      }

    case "AWAITING_REVISED_QUOTATION":
      return {
        key: "AWAITING_REVISED_QUOTATION",
        label: "Awaiting Revision",
        badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200/90 hover:bg-indigo-100/60",
        dotClass: "bg-indigo-500",
        title: "Revision in Progress",
        description: "Waiting for an updated quotation proposal",
        tooltipTheme: {
          cardClass: "bg-white border-indigo-200 shadow-xl shadow-indigo-950/10",
          headerClass: "bg-indigo-50/80 border-b border-indigo-100/90",
          headerTextClass: "text-indigo-950",
          tagClass: "text-indigo-800 bg-indigo-100/80 border-indigo-200/80",
          dotClass: "bg-indigo-500",
          descClass: "text-slate-600",
          detailDividerClass: "border-indigo-100/70",
          detailLabelClass: "text-slate-500",
          detailValueClass: "text-slate-900 font-semibold",
        },
      }

    case "SUPERSEDED":
      return {
        key: "SUPERSEDED",
        label: "Superseded",
        badgeClass: "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200/60",
        dotClass: "bg-slate-500",
        title: "Replaced by Revision",
        description: "A newer revision has replaced this quote",
        tooltipTheme: {
          cardClass: "bg-white border-slate-200 shadow-xl shadow-slate-950/10",
          headerClass: "bg-slate-50 border-b border-slate-200/80",
          headerTextClass: "text-slate-900",
          tagClass: "text-slate-700 bg-slate-100 border-slate-200",
          dotClass: "bg-slate-500",
          descClass: "text-slate-600",
          detailDividerClass: "border-slate-100",
          detailLabelClass: "text-slate-500",
          detailValueClass: "text-slate-900 font-semibold",
        },
      }



    default:
      return {
        key: statusKey,
        label: statusKey,
        badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
        dotClass: "bg-slate-400",
        title: statusKey,
        description: "Quotation status is being updated",
        tooltipTheme: {
          cardClass: "bg-white border-slate-200 shadow-xl shadow-slate-950/10",
          headerClass: "bg-slate-50 border-b border-slate-200/80",
          headerTextClass: "text-slate-900",
          tagClass: "text-slate-700 bg-slate-100 border-slate-200",
          dotClass: "bg-slate-400",
          descClass: "text-slate-600",
          detailDividerClass: "border-slate-100",
          detailLabelClass: "text-slate-500",
          detailValueClass: "text-slate-900 font-semibold",
        },
      }
  }
}

/**
 * Extracts contextual key-value pairs from a quotation item based on status.
 */
export function getQuotationStatusContextDetails(
  item: any,
  resolvedStatus: string,
  role: "admin" | "seller" | "buyer" = "admin"
): StatusContextDetail[] {
  if (!item) return []
  const details: StatusContextDetail[] = []
  const statusKey = (resolvedStatus || "PENDING").toUpperCase()

  // 1. Quote Amount
  const totalAmount = item.totalAmount ?? item.totalPrice ?? item.grandTotal
  if (totalAmount != null) {
    details.push({
      label: "Quote Amount",
      value: `${item.currencyCode || "INR"} ${Number(totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    })
  }

  // 2. Buyer or Supplier Party
  const buyerName =
    item.buyer?.company?.name ||
    item.buyer?.user?.name ||
    item.buyer?.name ||
    item.rfq?.buyer?.user?.name ||
    item.buyerName ||
    null

  const supplierName =
    item.company?.name ||
    item.company?.legalName ||
    item.seller?.company?.name ||
    item.seller?.user?.name ||
    item.sellerName ||
    item.companyName ||
    null

  if (role === "seller") {
    if (buyerName) {
      details.push({ label: "Buyer", value: buyerName })
    }
  } else if (role === "buyer") {
    if (supplierName) {
      details.push({ label: "Supplier", value: supplierName })
    }
  } else {
    // Admin: display both if available
    if (buyerName) details.push({ label: "Buyer", value: buyerName })
    if (supplierName) details.push({ label: "Supplier", value: supplierName })
  }

  // 3. Expiry / Valid Until
  const expiryDate = formatDateTime(item.validUntil || item.expiresAt)
  if (expiryDate) {
    details.push({
      label: "Valid Until",
      value: expiryDate,
    })
  }

  // 4. Status-specific fields (Reason, Revision notes)
  if (statusKey === "REJECTED") {
    const reason = item.rejectionReason || item.rejectReason || item.reason
    if (reason) {
      details.push({ label: "Reason", value: String(reason) })
    }
    const rejectedAt = formatDateTime(item.rejectedAt || item.updatedAt)
    if (rejectedAt) {
      details.push({ label: "Rejected At", value: rejectedAt })
    }
  }

  if (statusKey === "REQUEST_REVISION" || statusKey === "AWAITING_REVISED_QUOTATION") {
    const note = item.revisionNote || item.revisionMessage || item.feedback || item.comment
    if (note) {
      details.push({ label: "Feedback", value: String(note) })
    }
  }

  return details
}
